import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { channels, getChannel } from '@/mocks/channels';
import { getArticlesByChannel } from '@/mocks/articles';
import { ArticleCard } from '@/components/feature/ArticleCard';
import { toneClasses } from '@/components/feature/ChannelPill';

export default function ChannelPage() {
  const { slug = '' } = useParams();
  const channel = getChannel(slug);
  const channelList = getArticlesByChannel(slug);
  const [sort, setSort] = useState<'date' | 'views'>('date');

  useSeo({
    title: channel
      ? `${channel.name} - 最新${channel.name}资讯 | 热瓜速递`
      : '栏目不存在 - 热瓜速递',
    description: channel
      ? `${channel.name}：${channel.tagline}。${channel.desc}。热瓜速递为你网罗最新${channel.name}话题、热点事件与精彩讨论。`
      : '你访问的栏目不存在或已被调整。',
    keywords: channel ? `${channel.name},${channel.tagline},热瓜速递` : '',
    jsonLd: channel
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${channel.name} - 热瓜速递`,
            description: channel.desc,
            inLanguage: 'zh-CN',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
              { '@type': 'ListItem', position: 2, name: channel.name, item: `/channel/${channel.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!channel) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-bold text-foreground-900">这个栏目似乎不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">栏目可能已被调整，去首页看看其他新鲜瓜吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const sorted = [...channelList].sort((a, b) =>
    sort === 'date' ? b.date.localeCompare(a.date) : b.views - a.views,
  );

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-6">
      {/* breadcrumb */}
      <nav className="flex items-center gap-1.5 py-4 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-primary-600">
          <i className="ri-home-5-line" aria-hidden="true" /> 首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-600">{channel.name}</span>
      </nav>

      {/* 栏目头图 */}
      <section className="relative overflow-hidden rounded-lg">
        <div className="relative aspect-[16/6] w-full overflow-hidden">
          <img
            src={channel.cover}
            alt={`${channel.name}栏目背景`}
            title={`${channel.name}-热瓜速递`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
        </div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10">
          <span className={`inline-flex w-max items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClasses(channel.accent)}`}>
            <i className="ri-fire-fill" aria-hidden="true" /> 栏目频道
          </span>
          <h1 className="mt-3 text-2xl font-black text-background-50 md:text-4xl">{channel.name}</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
            {channel.desc}
          </p>
          <p className="mt-3 text-xs text-background-100/60">共收录 {channelList.length} 篇内容 · {channel.tagline}</p>
        </div>
      </section>

      {/* 列表 */}
      <section className="mt-6" aria-label={`${channel.name}内容列表`}>
        <div className="mb-4 flex items-center justify-between border-b border-background-200/70 pb-3">
          <h2 className="text-lg font-bold text-foreground-950">
            全部内容<span className="ml-2 text-sm font-normal text-foreground-400">（{channelList.length}）</span>
          </h2>
          <div className="inline-flex rounded-full border border-background-200/70 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setSort('date')}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                sort === 'date' ? 'bg-secondary-200 text-secondary-900' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              最新发布
            </button>
            <button
              type="button"
              onClick={() => setSort('views')}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                sort === 'views' ? 'bg-secondary-200 text-secondary-900' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              热度最高
            </button>
          </div>
        </div>

        {sorted.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {sorted.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-background-300/70 py-16 text-center text-sm text-foreground-400">
            该栏目正在蓄力中，好瓜马上就来
          </div>
        )}
      </section>

      {/* 其他栏目 */}
      <section className="mt-10 rounded-lg bg-background-100/70 p-5 md:p-6" aria-label="其他栏目">
        <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <i className="ri-swap-box-line text-primary-600" aria-hidden="true" /> 换个栏目吃瓜
        </h3>
        <div className="mt-4 flex flex-wrap gap-2.5">
          {channels
            .filter((c) => c.slug !== channel.slug)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/channel/${c.slug}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-medium transition-transform hover:-translate-y-0.5 ${toneClasses(
                  c.accent,
                )}`}
              >
                {c.name} <i className="ri-arrow-right-line text-xs" aria-hidden="true" />
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}